import type {
	AdminCreateOccasionBody,
	EventOccurrence,
	EventOccasion as SharedEventOccasion,
} from "../../../shared";

export interface OccasionImage {
	occasionImageId?: number;
	eventOccasionId?: number;
	picUrl: string;
	showOrder: number;
}

export interface AdminEventOccasion
	extends Omit<
		SharedEventOccasion,
		| "reservationStartAt"
		| "reservationEndAt"
		| "publishedAt"
		| "createdAt"
		| "updatedAt"
		| "deletedAt"
	> {
	reservationStartAt: string | null;
	reservationEndAt: string | null;
	publishedAt: string | null;
	createdAt?: string;
	updatedAt?: string;
	deletedAt?: string | null;
	occasionImages?: OccasionImage[];
	isCheckin?: boolean;
	occasionId?: number;
}

export interface AdminOccasionListResponse {
	data: AdminEventOccasion[];
	pagination: {
		page: number;
		pageSize: number;
		totalItems: number;
		totalPages: number;
	};
}

export type CreateOccasionPayload = AdminCreateOccasionBody;

const API_BASE = "/api/m/event";

export const getAdminEventOccasions = async (params?: {
	page?: number;
	pageSize?: number;
	eventCategoryId?: number;
	isPublished?: boolean;
	keyword?: string;
}): Promise<AdminOccasionListResponse> => {
	const searchParams = new URLSearchParams();
	if (params?.page) searchParams.set("page", String(params.page));
	if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));
	if (params?.eventCategoryId) {
		searchParams.set("eventCategoryId", String(params.eventCategoryId));
	}
	if (typeof params?.isPublished === "boolean") {
		searchParams.set("isPublished", String(params.isPublished));
	}
	if (params?.keyword) searchParams.set("keyword", params.keyword);

	const queryString = searchParams.toString();
	const path = queryString.length
		? `${API_BASE}/occasion?${queryString}`
		: `${API_BASE}/occasion`;

	const response = await fetch(path, {
		method: "GET",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const getOccasionTemplates = async (): Promise<AdminEventOccasion[]> => {
	const response = await fetch(`${API_BASE}/occasion?pageSize=100`, {
		method: "GET",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	const result = await response.json();
	return result.data || [];
};

export interface OccasionDetailResponse {
	occasion: AdminEventOccasion;
	occurrences: EventOccurrence[];
	cancellationPolicies: unknown[];
}

export const getOccasionDetail = async (
	occasionId: number,
): Promise<OccasionDetailResponse> => {
	const response = await fetch(`${API_BASE}/occasion/${occasionId}`, {
		method: "GET",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const createOccasion = async (
	payload: CreateOccasionPayload,
): Promise<AdminEventOccasion> => {
	const response = await fetch(`${API_BASE}/occasion`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(payload),
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const updateOccasion = async (
	occasionId: number,
	payload: Partial<CreateOccasionPayload>,
): Promise<AdminEventOccasion> => {
	const response = await fetch(`${API_BASE}/occasion/${occasionId}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(payload),
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const deleteOccasion = async (
	occasionId: number,
): Promise<{ success: boolean }> => {
	const response = await fetch(`${API_BASE}/occasion/${occasionId}`, {
		method: "DELETE",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};
